// frontend/src/utils/reportBlocks.ts
//
// Agrupación y filtrado de los reports de cliente, que llegan como una tabla
// plana con filas jerárquicas: `tipoFila` dice qué es cada fila y
// (ordenBloque, ordenDetalle) dan el orden, con ordenDetalle === 0 marcando la
// cabecera de cada bloque.
//
// La razón de que esto viva aparte de los componentes es el filtrado: filtrar
// fila a fila deja sublíneas huérfanas colgando sin su cabecera, y una cabecera
// sin su detalle miente sobre lo que agrega. La unidad de filtrado tiene que ser
// el bloque completo.
import type { ReportRow } from '@/types'

export interface ReportBlock<T> {
  /** Valor de `ordenBloque`, que es único por bloque en las tres vistas. */
  key: number
  header: T
  details: T[]
}

/** Predicado sobre una fila. `null` significa «este filtro no está activo». */
export type RowPredicate<T> = ((row: T) => boolean) | null

/**
 * Agrupa las filas planas en bloques.
 *
 * Asume que llegan **ya ordenadas** por (ordenBloque, ordenDetalle), que es
 * responsabilidad del backend: las vistas de Fabric no pueden llevar ORDER BY
 * propio. No reordena por su cuenta, para que el orden de pantalla sea
 * exactamente el que da el origen.
 *
 * Un bloque sin cabecera (ordenDetalle nunca 0) se descarta: no habría dónde
 * colgar sus sublíneas ni qué mostrar colapsado. No debería pasar con estas
 * vistas, y si pasa es mejor perder el bloque que pintar filas sin contexto.
 */
export function groupBlocks<T extends ReportRow>(rows: T[]): ReportBlock<T>[] {
  const blocks: ReportBlock<T>[] = []
  const byKey = new Map<number, ReportBlock<T>>()
  const orphans: T[] = []

  for (const row of rows) {
    if (row.ordenDetalle === 0) {
      const block: ReportBlock<T> = { key: row.ordenBloque, header: row, details: [] }
      byKey.set(row.ordenBloque, block)
      blocks.push(block)
      continue
    }
    const block = byKey.get(row.ordenBloque)
    if (block) block.details.push(row)
    else orphans.push(row)
  }

  // Las sublíneas que llegan antes de su cabecera se recolocan: el orden del
  // backend las pone después, pero si alguna vez no fuera así, perderlas en
  // silencio sería un fallo difícil de ver.
  for (const row of orphans) {
    byKey.get(row.ordenBloque)?.details.push(row)
  }

  return blocks
}

/**
 * Filtra bloques conservándolos **completos**.
 *
 * Un bloque entra si su cabecera cumple todos los predicados de cabecera y, si
 * hay predicados de detalle activos, si **alguna** de sus sublíneas los cumple.
 * Cuando entra se devuelve entero, cabecera y todas sus sublíneas: buscar un
 * artículo debe mostrar su cabecera con su detalle, no sublíneas sueltas.
 *
 * Los predicados `null` se ignoran, así que la lista puede construirse con
 * huecos según qué filtros estén rellenos.
 */
export function filterBlocks<T extends ReportRow>(
  blocks: ReportBlock<T>[],
  headerPredicates: RowPredicate<T>[] = [],
  detailPredicates: RowPredicate<T>[] = [],
): ReportBlock<T>[] {
  const headerActive = headerPredicates.filter(Boolean) as ((row: T) => boolean)[]
  const detailActive = detailPredicates.filter(Boolean) as ((row: T) => boolean)[]

  if (headerActive.length === 0 && detailActive.length === 0) return blocks

  return blocks.filter(block => {
    if (!headerActive.every(p => p(block.header))) return false
    if (detailActive.length === 0) return true
    // Un solo detalle que cumpla basta: el usuario busca el bloque que contiene
    // eso, no los bloques cuyo detalle entero lo cumple.
    return block.details.some(d => detailActive.every(p => p(d)))
  })
}

/**
 * Bloques que abren solos su detalle porque alguna sublínea es el motivo de que
 * estén en la lista.
 *
 * Es el mismo gesto que la lista de pedidos de venta: si el pedido sale porque
 * contiene el artículo buscado, se abre por ese artículo en lugar de obligar a
 * desplegarlo a mano para averiguar cuál de sus catorce líneas casaba.
 *
 * Sin coincidencia (`coincide` a `null`, que es lo que devuelve el matcher cuando
 * no hay filtros de detalle) no se abre nada.
 *
 * No hay tope de bloques abiertos: la página trae 50 como mucho y desplegarlos
 * todos es una acción que la pantalla ya ofrece con «Desplegar todo».
 */
export function bloquesConCoincidencia<T extends ReportRow>(
  blocks: ReportBlock<T>[],
  coincide: ((row: T) => boolean) | null,
): Set<number> {
  const out = new Set<number>()
  if (!coincide) return out
  for (const block of blocks) {
    if (block.details.some(coincide)) out.add(block.key)
  }
  return out
}

/** Devuelve las filas de los bloques en orden de pantalla: cabecera, su detalle, siguiente. */
export function flattenBlocks<T extends ReportRow>(blocks: ReportBlock<T>[]): T[] {
  const out: T[] = []
  for (const block of blocks) {
    out.push(block.header)
    out.push(...block.details)
  }
  return out
}

/**
 * Filas de un único `tipoFila` dentro de los bloques dados.
 *
 * Es lo que se pasa a la agregación de columnas. Acotar a un solo tipo no es un
 * detalle de implementación: en estos reports la cabecera **ya es la suma de su
 * detalle**, así que agregar los dos niveles a la vez duplicaría todo.
 */
export function rowsOfType<T extends ReportRow>(
  blocks: ReportBlock<T>[],
  tipoFila: string,
): T[] {
  const out: T[] = []
  for (const block of blocks) {
    if (block.header.tipoFila === tipoFila) out.push(block.header)
    for (const d of block.details) {
      if (d.tipoFila === tipoFila) out.push(d)
    }
  }
  return out
}

/**
 * Valores distintos y no vacíos de un campo, ordenados, para poblar los `select`
 * de los filtros desde el propio dataset en lugar de una constante que se queda
 * desfasada.
 */
/** Valores distintos de `field` junto al primer `descripcionField` no vacío.
 *
 *  Un desplegable de códigos a secas obliga a saberse la referencia de memoria.
 *  Se queda con la PRIMERA descripción no vacía de cada código: la vista puede
 *  traer la misma referencia en varias filas y alguna con el nombre en blanco. */
export function distinctPairs<T>(
  rows: T[], field: keyof T & string, descripcionField?: string,
): { valor: string; detalle?: string }[] {
  const mapa = new Map<string, string | undefined>()
  for (const row of rows) {
    const raw = (row as Record<string, unknown>)[field]
    if (raw == null || raw === '') continue
    const clave = String(raw)
    const previa = mapa.get(clave)
    if (previa) continue
    const desc = descripcionField
      ? (row as Record<string, unknown>)[descripcionField]
      : undefined
    mapa.set(clave, desc == null || desc === '' ? previa : String(desc))
  }
  return [...mapa.entries()]
    .map(([valor, detalle]) => ({ valor, detalle }))
    .sort((a, b) => a.valor.localeCompare(b.valor, 'es'))
}

export function distinctValues<T>(rows: T[], field: keyof T & string): string[] {
  const seen = new Set<string>()
  for (const row of rows) {
    const raw = (row as Record<string, unknown>)[field]
    if (raw == null || raw === '') continue
    seen.add(String(raw))
  }
  return [...seen].sort((a, b) => a.localeCompare(b, 'es'))
}

/**
 * Valores de `tipoFila` que el registro del report NO reconoce.
 *
 * Un nivel desconocido no rompe nada de forma visible: la fila se pinta igual
 * —los bloques cuelgan de `ordenBloque`/`ordenDetalle`, no del tipo—, pero
 * ninguna columna de detalle la reclama y su subtabla sale vacía. Sin nada que
 * lo delate, eso se diagnostica mirando el SQL de la vista.
 *
 * Y pasa de verdad. En la migración del vocabulario al inglés, `tipo_fila`
 * estaba declarada `VARCHAR(12)` y `FINISHED_GOOD` mide 13: habría llegado como
 * `FINISHED_GOO`, sin error de nadie y sin coincidir con ningún nivel. Se cazó
 * en la vista antes de desplegar; esto es para la próxima, que no se cazará.
 */
export function nivelesDesconocidos<T extends ReportRow>(
  rows: T[],
  niveles: readonly string[],
): string[] {
  const conocidos = new Set(niveles)
  const raros = new Set<string>()
  for (const row of rows) {
    const tipo = String(row.tipoFila ?? '')
    if (tipo !== '' && !conocidos.has(tipo)) raros.add(tipo)
  }
  return [...raros].sort((a, b) => a.localeCompare(b, 'es'))
}
